import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Stethoscope } from "lucide-react";
import { Reveal, Overline } from "@/components/landing/primitives";
import { RELATED_ARTICLES } from "@/content/site";
import { ARTICLES_BY_SLUG } from "@/content/approfondimenti";

const DISCLAIMER =
  "Il risultato di questo percorso ha esclusivamente finalità informative e orientative e non costituisce una diagnosi né un'indicazione terapeutica. La scelta del trattamento viene definita dopo valutazione medica individuale.";

// helper builders
const q = (title, options, subtitle) => ({ kind: "q", title, subtitle, options });
const r = (title, text, approaches, href, hrefLabel, alert) => ({ kind: "r", title, text, approaches, href, hrefLabel, alert });

const NODES = {
  area: q("Quale area vorresti migliorare?", [
    { label: "Viso", to: "viso_problema" },
    { label: "Corpo", to: "corpo_problema" },
    { label: "Capelli", to: "capelli_problema" },
  ]),

  // ---------- VISO ----------
  viso_problema: q("Qual è il problema che vorresti migliorare principalmente?", [
    { label: "Macchie e discromie", to: "viso_macchie_q" },
    { label: "Rossori e capillari", to: "viso_rossori_q" },
    { label: "Cicatrici", to: "viso_cicatrici_q" },
    { label: "Pori e texture irregolare", to: "res_pori" },
    { label: "Rughe e qualità della pelle", to: "viso_rughe_q" },
    { label: "Lassità e cedimento", to: "viso_lassita_q" },
    { label: "Peli superflui", to: "viso_peli_q" },
    { label: "Non saprei definirlo", to: "viso_aiuto_q" },
  ]),
  viso_peli_q: q("In quale zona del viso?", [
    { label: "Labbro superiore", to: "res_epilazione" },
    { label: "Mento", to: "res_epilazione" },
    { label: "Guance / basette", to: "res_epilazione" },
    { label: "Collo", to: "res_epilazione" },
    { label: "Più zone", to: "res_epilazione" },
  ]),
  viso_macchie_q: q("Come si presentano prevalentemente?", [
    { label: "Macchie marroni isolate", to: "viso_macchie_foto_q" },
    { label: "Colorito non uniforme / più macchie diffuse", to: "viso_macchie_foto_q" },
    { label: "Comparse dopo il sole", to: "viso_macchie_foto_q" },
    { label: "Comparse dopo un'infiammazione", to: "viso_macchie_foto_q" },
    { label: "Melasma già diagnosticato", to: "viso_macchie_foto_q" },
    { label: "Non saprei", to: "viso_macchie_foto_q" },
  ]),
  viso_macchie_foto_q: q("Come descriveresti la tua carnagione?", [
    { label: "Chiara, si scotta facilmente", to: "res_macchie_chiara" },
    { label: "Media / olivastra", to: "res_macchie_scura" },
    { label: "Scura", to: "res_macchie_scura" },
    { label: "Non saprei", to: "res_macchie" },
  ], "Il fototipo influenza la scelta e la sicurezza dei trattamenti sulle macchie."),
  viso_rossori_q: q("Il problema è prevalentemente:", [
    { label: "Singoli capillari visibili", to: "res_rossori" },
    { label: "Rossore diffuso", to: "res_rossori" },
    { label: "Episodi di flushing", to: "res_rossori" },
    { label: "Piccole lesioni vascolari / angiomi", to: "res_rossori" },
    { label: "Rosacea già diagnosticata", to: "res_rossori" },
    { label: "Non saprei", to: "res_rossori" },
  ]),
  viso_cicatrici_q: q("Qual è principalmente l'origine delle cicatrici?", [
    { label: "Acne", to: "viso_cicatrici_tipo_q" },
    { label: "Intervento chirurgico", to: "viso_cicatrici_tipo_q" },
    { label: "Trauma", to: "viso_cicatrici_tipo_q" },
    { label: "Non saprei", to: "viso_cicatrici_tipo_q" },
  ]),
  viso_cicatrici_tipo_q: q("Come appare principalmente la cicatrice?", [
    { label: "Depressa / incavata", to: "res_cicatrici_atrofiche" },
    { label: "In rilievo / spessa", to: "res_cicatrici_rilievo" },
    { label: "Arrossata o recente", to: "res_cicatrici_rosse" },
    { label: "Chiara e matura", to: "res_cicatrici_chiare" },
    { label: "Non saprei", to: "res_cicatrici" },
  ], "L'aspetto della cicatrice orienta l'approccio; la classificazione precisa richiede la visita."),
  viso_rughe_q: q("Cosa vorresti migliorare principalmente?", [
    { label: "Pelle spenta", to: "res_skinquality" },
    { label: "Rughe fini", to: "res_skinquality" },
    { label: "Disidratazione", to: "res_skinquality" },
    { label: "Pori / grana irregolare", to: "res_skinquality" },
    { label: "Perdita di elasticità", to: "res_skinquality" },
    { label: "Più problemi contemporaneamente", to: "res_skinquality" },
  ]),
  viso_lassita_q: q("Dove noti maggiormente il problema?", [
    { label: "Terzo inferiore / jawline", to: "res_lassita_viso" },
    { label: "Guance", to: "res_lassita_viso" },
    { label: "Collo", to: "res_lassita_viso" },
    { label: "Contorno occhi", to: "res_lassita_viso" },
    { label: "Più aree", to: "res_lassita_viso" },
  ]),
  viso_aiuto_q: q("Cosa ti disturba maggiormente quando ti guardi allo specchio?", [
    { label: "Il colorito non è uniforme", to: "res_macchie" },
    { label: "Vedo capillari o rossori", to: "res_rossori" },
    { label: "La pelle è spenta o poco compatta", to: "res_skinquality" },
    { label: "Segni o cicatrici", to: "res_cicatrici" },
    { label: "Il viso mi sembra \u201csceso\u201d", to: "res_lassita_viso" },
  ], "Scegli la descrizione più vicina a ciò che noti."),

  // ---------- CORPO ----------
  corpo_problema: q("Qual è il problema che vorresti migliorare principalmente?", [
    { label: "Adiposità localizzata", to: "corpo_adip_q" },
    { label: "Cellulite", to: "corpo_cell_q" },
    { label: "Gonfiore / ritenzione", to: "res_ritenzione" },
    { label: "Lassità cutanea", to: "corpo_lass_q" },
    { label: "Ridotto tono muscolare", to: "res_tono" },
    { label: "Smagliature", to: "corpo_smag_zona_q" },
    { label: "Peli superflui", to: "corpo_peli_q" },
    { label: "Più problemi / Non saprei", to: "res_corpo_valutazione" },
  ]),
  corpo_peli_q: q("In quale zona del corpo?", [
    { label: "Ascelle", to: "res_epilazione" },
    { label: "Inguine", to: "res_epilazione" },
    { label: "Gambe", to: "res_epilazione" },
    { label: "Braccia", to: "res_epilazione" },
    { label: "Schiena / spalle", to: "res_epilazione" },
    { label: "Più zone", to: "res_epilazione" },
  ]),
  corpo_adip_q: q("Dove è localizzato principalmente l'accumulo?", [
    { label: "Addome", to: "res_adiposita" },
    { label: "Fianchi", to: "res_adiposita" },
    { label: "Culotte de cheval / regione trocanterica", to: "res_adiposita" },
    { label: "Interno coscia", to: "res_adiposita" },
    { label: "Braccia", to: "res_adiposita" },
    { label: "Altra zona", to: "res_adiposita" },
  ]),
  corpo_cell_q: q("Il tessuto si presenta prevalentemente:", [
    { label: "Morbido / gonfio", to: "res_cellulite" },
    { label: "Irregolare", to: "res_cellulite" },
    { label: "Duro / fibrotico", to: "res_cellulite" },
    { label: "Con noduli", to: "res_cellulite" },
    { label: "Associato a lassità", to: "res_cellulite" },
    { label: "Non saprei", to: "res_cellulite" },
  ]),
  corpo_lass_q: q("Dove noti maggiormente la lassità?", [
    { label: "Addome", to: "res_body_firming" },
    { label: "Braccia", to: "res_body_firming" },
    { label: "Interno coscia", to: "res_body_firming" },
    { label: "Ginocchia", to: "res_body_firming" },
    { label: "Altro", to: "res_body_firming" },
  ]),
  corpo_smag_zona_q: q("Dove sono localizzate prevalentemente?", [
    { label: "Addome", to: "corpo_smag_colore_q" },
    { label: "Fianchi", to: "corpo_smag_colore_q" },
    { label: "Cosce", to: "corpo_smag_colore_q" },
    { label: "Glutei", to: "corpo_smag_colore_q" },
    { label: "Seno", to: "corpo_smag_colore_q" },
    { label: "Braccia", to: "corpo_smag_colore_q" },
    { label: "Più zone", to: "corpo_smag_colore_q" },
  ]),
  corpo_smag_colore_q: q("Di che colore sono prevalentemente?", [
    { label: "Rosse / violacee", to: "res_smagliature_rosse" },
    { label: "Bianche / madreperlacee", to: "res_smagliature_bianche" },
    { label: "Miste", to: "res_smagliature" },
    { label: "Non saprei", to: "res_smagliature" },
  ], "Il colore indica in genere lo stadio di maturazione della smagliatura."),

  // ---------- CAPELLI ----------
  capelli_problema: q("Qual è il problema principale?", [
    { label: "Caduta aumentata recentemente", to: "res_capelli" },
    { label: "Diradamento progressivo", to: "res_capelli" },
    { label: "Attaccatura / tempie", to: "res_capelli" },
    { label: "Vertex", to: "res_capelli" },
    { label: "Capelli più sottili", to: "res_capelli" },
    { label: "Prurito / desquamazione / cuoio irritato", to: "res_capelli" },
    { label: "Non saprei", to: "res_capelli" },
  ]),

  // ---------- RISULTATI ----------
  res_macchie: r(
    "Macchie e discromie",
    "Le macchie cutanee non sono tutte uguali. Origine, profondità del pigmento, fototipo e distribuzione delle lesioni determinano quale approccio possa essere più appropriato.",
    ["Laser per lesioni pigmentate selezionate", "IPL nei quadri indicati", "Peeling", "Skincare e fotoprotezione", "Protocolli specifici in presenza di melasma"],
    "/trattamenti-laser/macchie-discromie",
    "Approfondisci macchie e discromie"
  ),
  res_macchie_chiara: r(
    "Macchie · carnagione chiara",
    "Sui fototipi chiari i trattamenti mirati al pigmento hanno in genere un margine di sicurezza più ampio, ma la scelta dipende comunque da tipo, profondità e origine delle macchie.",
    ["Laser per lesioni pigmentate selezionate", "IPL nei quadri indicati", "Peeling", "Skincare e fotoprotezione", "Protocolli specifici in presenza di melasma"],
    "/trattamenti-laser/macchie-discromie",
    "Approfondisci macchie e discromie"
  ),
  res_macchie_scura: r(
    "Macchie · carnagione media o scura",
    "Sui fototipi più scuri è necessaria maggiore prudenza: alcuni trattamenti possono aumentare il rischio di discromie post-infiammatorie. La valutazione medica è particolarmente importante e spesso si preferiscono approcci graduali.",
    ["Approcci selettivi e a bassa aggressività", "Peeling mirati quando indicati", "Skincare e fotoprotezione rigorosa", "Protocolli specifici in presenza di melasma", "Valutazione medica prima del trattamento"],
    "/trattamenti-laser/macchie-discromie",
    "Approfondisci macchie e discromie"
  ),
  res_rossori: r(
    "Capillari e rossori",
    "Il tipo di trattamento dipende da distribuzione, calibro e profondità dei vasi e dalla presenza di una componente diffusa o infiammatoria. In presenza di rosacea, il trattamento vascolare può rappresentare solo una parte del percorso.",
    ["Laser vascolari", "532 nm per vasi superficiali selezionati", "1064 nm per alcuni vasi più profondi", "IPL nei quadri di rossore diffuso", "Skincare mirata alla barriera cutanea"],
    "/trattamenti-laser/capillari-rossori",
    "Approfondisci capillari e rossori"
  ),
  res_cicatrici: r(
    "Cicatrici",
    "Il trattamento delle cicatrici viene scelto in base a tipo, profondità, colore, maturità e caratteristiche del tessuto.",
    ["Laser frazionati", "CO2 / Er:YAG", "Laser non ablativi", "Radiofrequenza ad aghi", "Trattamenti rigenerativi quando indicati"],
    "/cicatrici",
    "Approfondisci cicatrici"
  ),
  res_cicatrici_atrofiche: r(
    "Cicatrici atrofiche (depresse)",
    "Le cicatrici depresse — spesso esito di acne — variano per profondità, tipo (a punteruolo, rolling, boxcar) ed estensione. La scelta del trattamento si definisce dopo valutazione diretta.",
    ["Laser frazionati ablativi (CO2 / Er:YAG)", "Laser frazionati non ablativi", "Radiofrequenza ad aghi", "Subcision nei casi indicati", "Trattamenti rigenerativi quando utili"],
    "/cicatrici",
    "Approfondisci cicatrici"
  ),
  res_cicatrici_rilievo: r(
    "Cicatrici in rilievo (ipertrofiche / cheloidi)",
    "Le cicatrici in rilievo richiedono un approccio prudente: alcune tecniche aggressive possono peggiorarle. La distinzione tra cicatrice ipertrofica e cheloide, e la relativa gestione, richiede una valutazione medica.",
    ["Medicazioni e gel al silicone", "Infiltrazioni quando indicate", "Laser vascolare sulla componente arrossata", "Laser frazionati in casi selezionati e con cautela", "Valutazione medica prima del trattamento"],
    "/cicatrici",
    "Approfondisci cicatrici",
    "Le cicatrici cheloidee hanno un comportamento particolare e possono recidivare o peggiorare con trattamenti non appropriati: è indispensabile una valutazione medica prima di qualsiasi trattamento."
  ),
  res_cicatrici_rosse: r(
    "Cicatrici arrossate o recenti",
    "Il rossore indica in genere una cicatrice ancora in fase di maturazione. Spesso è utile agire sulla componente vascolare e rispettare i tempi di maturazione del tessuto.",
    ["Laser vascolare per la componente arrossata", "Fotoprotezione rigorosa", "Medicazioni al silicone", "Rivalutazione nel tempo prima di trattamenti più profondi"],
    "/cicatrici",
    "Approfondisci cicatrici"
  ),
  res_cicatrici_chiare: r(
    "Cicatrici chiare e mature",
    "Le cicatrici chiare e stabilizzate possono essere trattate per migliorarne texture e rilievo; il risultato dipende da profondità e caratteristiche del tessuto.",
    ["Laser frazionati", "Radiofrequenza ad aghi", "Needling nei casi indicati", "Trattamenti rigenerativi quando utili"],
    "/cicatrici",
    "Approfondisci cicatrici"
  ),
  res_pori: r(
    "Pori e texture · Skin Quality",
    "Il trattamento dipende da qualità della pelle, pori, fotodanno, cicatrici superficiali e downtime accettabile.",
    ["Er:YAG", "CO2 light / frazionato", "Laser non ablativi", "Radiofrequenza ad aghi", "Peeling o skinbooster quando indicati"],
    "/skin-quality/laser-qualita-cutanea",
    "Approfondisci Skin Quality"
  ),
  res_skinquality: r(
    "Skin Quality",
    "Spesso il percorso combina tecnologie fisiche e trattamenti biologici, senza necessariamente modificare i volumi del volto.",
    ["Laser frazionati non ablativi", "Radiofrequenza", "Biostimolazione / skinbooster", "Peeling", "Skincare e fotoprotezione"],
    "/skin-quality/laser-qualita-cutanea",
    "Approfondisci Skin Quality"
  ),
  res_lassita_viso: r(
    "Lassità e cedimento",
    "Le tecnologie non chirurgiche non sostituiscono un lifting chirurgico nei casi di lassità importante, ma possono agire sulla compattezza dei tessuti.",
    ["HIFU / Ultraformer", "Radiofrequenza", "Radiofrequenza ad aghi", "Fili di trazione nei casi indicati", "Supporti biologici quando utili"],
    "/skin-quality/ultrasuoni",
    "Approfondisci qualità e compattezza"
  ),
  res_adiposita: r(
    "Adiposità localizzate · Body Reset",
    "L'adiposità localizzata va distinta dal grasso viscerale e dal semplice aumento di peso. La valutazione serve a comprendere quantità e distribuzione del pannicolo sottocutaneo e l'eventuale presenza di lassità, cellulite o ritenzione.",
    ["Tecnologie di rimodellamento", "PowerShape", "Criolipolisi nei casi indicati", "Trattamenti iniettivi selezionati", "Associazione con tightening (Body Reset)"],
    "/corpo/adiposita-localizzate",
    "Approfondisci adiposità localizzate"
  ),
  res_cellulite: r(
    "Cellulite & Drainage · Body Reset",
    "Cellulite, ritenzione e adiposità localizzata sono condizioni differenti e possono richiedere approcci diversi.",
    ["PowerShape", "ZWave / onde d'urto", "Mesoterapia quando indicata", "Radiofrequenza / tightening se è presente lassità"],
    "/corpo/cellulite",
    "Approfondisci cellulite e corpo"
  ),
  res_ritenzione: r(
    "Gonfiore e ritenzione",
    "L'orientamento è verso la valutazione della componente edematosa/ritentiva, del microcircolo e della qualità del tessuto.",
    ["PowerShape", "Mesoterapia drenante quando indicata", "Valutazione del microcircolo e del tessuto"],
    "/corpo/edema-microcircolo",
    "Approfondisci corpo",
    "Se il gonfiore è importante, doloroso, asimmetrico o associato ad altri sintomi, il semplice trattamento estetico può non essere appropriato: è necessaria una valutazione medica."
  ),
  res_body_firming: r(
    "Body Firming · Body Reset",
    "Quando è presente lassità cutanea, l'obiettivo è migliorare compattezza e qualità del tessuto, eventualmente in associazione al trattamento del pannicolo.",
    ["Radiofrequenza", "XERF o tecnologie disponibili", "Trattamenti biostimolanti", "Associazione con trattamento del pannicolo quando necessario"],
    "/corpo/lassita",
    "Approfondisci corpo"
  ),
  res_tono: r(
    "Tono e rimodellamento · Body Reset",
    "Non si tratta di un trattamento per perdere peso, ma di un lavoro sul tono e sul rimodellamento locale.",
    ["Emsculpt Neo / HIFEM-RF nei casi indicati", "Associazione con altri moduli Body Reset quando utile"],
    "/corpo",
    "Approfondisci corpo"
  ),
  res_smagliature: r(
    "Smagliature",
    "Il trattamento dipende da colore, maturità, profondità e fototipo delle smagliature.",
    ["Laser frazionati", "CO2 / Er:YAG", "1540 / Multifrax", "RF ad aghi", "Supporti biologici quando indicati"],
    "/trattamenti-laser/smagliature",
    "Approfondisci trattamenti laser"
  ),
  res_smagliature_rosse: r(
    "Smagliature rosse (recenti)",
    "Le smagliature rosse-violacee sono in genere più recenti: è spesso la fase in cui i trattamenti danno le risposte migliori, agendo anche sulla componente vascolare.",
    ["Laser vascolare / a colorante", "Laser frazionati non ablativi", "1540 / Multifrax", "Radiofrequenza ad aghi", "Skincare di supporto"],
    "/trattamenti-laser/smagliature",
    "Approfondisci trattamenti laser"
  ),
  res_smagliature_bianche: r(
    "Smagliature bianche (mature)",
    "Le smagliature bianche sono stabilizzate: i risultati tendono a essere più graduali e mirano a migliorare texture e compattezza del tessuto.",
    ["Laser frazionati (CO2 / Er:YAG)", "Radiofrequenza ad aghi", "1540 / Multifrax", "Needling nei casi indicati", "Trattamenti biostimolanti di supporto"],
    "/trattamenti-laser/smagliature",
    "Approfondisci trattamenti laser"
  ),
  res_corpo_valutazione: r(
    "Valutazione del corpo",
    "Quando i quadri sono misti o poco definiti, il primo passo è una valutazione che distingua adiposità, cellulite, ritenzione e lassità, per costruire un percorso su misura.",
    ["Analisi del tessuto e del pannicolo", "Tecnologie Body Reset in combinazione", "Percorso personalizzato dopo la visita"],
    "/corpo",
    "Approfondisci corpo"
  ),
  res_epilazione: r(
    "Epilazione laser",
    "L'epilazione laser agisce sul pigmento del pelo ed è generalmente più efficace in presenza di un buon contrasto tra pelo e cute. Colore e spessore del pelo, fototipo e fase di crescita influenzano la risposta e il numero di sedute; è sempre preceduta da una valutazione medica.",
    ["Epilazione laser medicale", "Protocolli per peli incarniti e follicolite", "Valutazione di fototipo e tipo di pelo", "Programmazione delle sedute in base al ciclo del pelo"],
    "/trattamenti-laser/epilazione",
    "Approfondisci epilazione laser"
  ),
  res_capelli: r(
    "Tricologia",
    "Prima di scegliere il trattamento è necessario distinguere effluvium, miniaturizzazione progressiva, alopecia androgenetica, condizioni infiammatorie del cuoio capelluto ed eventuali cause sistemiche o carenziali. Questo percorso non fornisce una diagnosi.",
    ["Visita tricologica e tricoscopia", "Eventuali esami", "PRP e trattamenti iniettivi", "Laser / tecnologie disponibili", "Terapia domiciliare quando indicata"],
    "/tricologia",
    "Approfondisci tricologia"
  ),
};

function ProgressBar({ step, total }) {
  const pct = Math.min(100, Math.round((step / total) * 100));
  return (
    <div className="mb-10" data-testid="ddi-progress">
      <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-2">Domanda {step} di {total}</p>
      <div className="h-px w-full bg-ink/10">
        <div className="h-px bg-sagedark transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function DaDoveIniziare() {
  const [id, setId] = useState("start");
  const [hist, setHist] = useState([]);

  const go = (next) => {
    setHist((h) => [...h, id]);
    setId(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setHist((h) => {
      const copy = [...h];
      const prev = copy.pop();
      if (prev !== undefined) setId(prev);
      return copy;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const restart = () => {
    setHist([]);
    setId("start");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const node = id === "start" ? null : NODES[id];
  const relatedArticles =
    node && node.kind === "r" && node.href
      ? (RELATED_ARTICLES[node.href.replace(/^\//, "")] ||
          RELATED_ARTICLES[node.href.replace(/^\//, "").split("/")[0]] ||
          [])
          .map((s) => ARTICLES_BY_SLUG[s])
          .filter(Boolean)
          .slice(0, 2)
      : [];
  const questionsAnswered = hist.filter((h) => h !== "start" && NODES[h] && NODES[h].kind === "q").length;
  const stepNumber = questionsAnswered + 1;
  const estTotal = Math.max(3, stepNumber);

  return (
    <div className="bg-canvas text-ink min-h-screen">
      <section className="pt-36 md:pt-44 pb-20 md:pb-28">
        <div className="max-w-[820px] mx-auto px-6 md:px-10">
          <AnimatePresence mode="wait">
            {/* HERO */}
            {id === "start" && (
              <motion.div key="hero" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.5 }}>
                <Overline>Da dove iniziare?</Overline>
                <h1 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                  Non sai quale trattamento scegliere?
                </h1>
                <p className="mt-6 font-serif text-xl md:text-2xl font-light text-ink/65">
                  Non partire dalla tecnologia. Parti dal problema.
                </p>
                <p className="mt-6 text-[15px] md:text-base leading-relaxed text-ink/70 max-w-2xl">
                  Rispondi ad alcune semplici domande per individuare l'area più utile da approfondire.
                  Il percorso ha una funzione orientativa e non sostituisce la valutazione medica.
                </p>
                <div className="mt-9 border-l-2 border-sage bg-sand p-5 max-w-2xl">
                  <p className="text-[13px] text-ink/70 leading-relaxed">{DISCLAIMER}</p>
                </div>
                <button onClick={() => go("area")} data-testid="ddi-start" className="group relative inline-flex items-center overflow-hidden border border-ink px-9 py-4 text-xs uppercase tracking-[0.2em] mt-10">
                  <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
                  <span className="relative group-hover:text-canvas transition-colors duration-500 inline-flex items-center gap-2">Inizia il percorso <ArrowRight className="w-4 h-4" /></span>
                </button>
              </motion.div>
            )}

            {/* QUESTION */}
            {node && node.kind === "q" && (
              <motion.div key={id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} data-testid="ddi-question">
                <ProgressBar step={stepNumber} total={estTotal} />
                <h2 className="font-serif font-light text-2xl md:text-4xl leading-tight tracking-tight max-w-2xl">{node.title}</h2>
                {node.subtitle && <p className="mt-3 text-sm text-ink/55">{node.subtitle}</p>}
                <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {node.options.map((o) => (
                    <button
                      key={o.label}
                      onClick={() => go(o.to)}
                      data-testid={`ddi-option-${o.to}`}
                      className="group flex items-center justify-between gap-3 text-left border border-ink/15 bg-canvas px-5 py-4 text-[15px] text-ink/85 hover:border-ink hover:bg-sand transition-all duration-300"
                    >
                      <span>{o.label}</span>
                      <ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0 transition-all duration-300 shrink-0" />
                    </button>
                  ))}
                </div>
                <div className="mt-10 flex flex-wrap items-center gap-5 text-sm">
                  <button onClick={back} data-testid="ddi-back" className="inline-flex items-center gap-2 text-ink/60 hover:text-ink"><ArrowLeft className="w-4 h-4" /> Indietro</button>
                  <button onClick={restart} className="inline-flex items-center gap-2 text-ink/45 hover:text-ink"><RotateCcw className="w-3.5 h-3.5" /> Ricomincia</button>
                </div>
              </motion.div>
            )}

            {/* RESULT */}
            {node && node.kind === "r" && (
              <motion.div key={id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.45 }} data-testid="ddi-result">
                <Overline>Orientamento</Overline>
                <p className="mt-5 text-sm text-ink/55">Dalle risposte fornite, l'area che potrebbe essere più utile approfondire è</p>
                <h2 className="mt-2 font-serif font-light text-3xl md:text-5xl leading-tight tracking-tight">{node.title}</h2>
                <p className="mt-6 text-[15px] md:text-base leading-relaxed text-ink/75 max-w-2xl">{node.text}</p>

                {node.alert && (
                  <div className="mt-7 border-l-2 border-sagedark bg-sand p-5 max-w-2xl">
                    <p className="text-[13px] text-ink/75 leading-relaxed">
                      {node.alert}
                    </p>
                  </div>
                )}

                <div className="mt-8">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-sagedark mb-4">Possibili approcci da valutare</p>
                  <ul className="space-y-2.5 max-w-xl">
                    {node.approaches.map((a) => (
                      <li key={a} className="flex items-start gap-3 text-[15px] text-ink/80">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sagedark shrink-0" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to={node.href} data-testid="ddi-cta-approfondisci" className="group relative inline-flex items-center overflow-hidden border border-ink px-8 py-3.5 text-xs uppercase tracking-[0.18em]">
                    <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
                    <span className="relative group-hover:text-canvas transition-colors duration-500 inline-flex items-center gap-2">{node.hrefLabel} <ArrowRight className="w-4 h-4" /></span>
                  </Link>
                  <Link to="/contatti" data-testid="ddi-cta-prenota" className="inline-flex items-center gap-2 border border-ink/20 px-8 py-3.5 text-xs uppercase tracking-[0.18em] text-ink/75 hover:border-sagedark hover:text-sagedark transition-colors duration-400">
                    <Stethoscope className="w-4 h-4" /> Prenota una valutazione
                  </Link>
                </div>

                {relatedArticles.length > 0 && (
                  <div className="mt-12 border-t border-ink/10 pt-8" data-testid="ddi-related">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-sagedark mb-5">Vuoi capire meglio? Leggi</p>
                    <div className={`grid grid-cols-1 gap-3 ${relatedArticles.length === 2 ? "sm:grid-cols-2" : ""}`}>
                      {relatedArticles.map((a) => (
                        <Link
                          key={a.slug}
                          to={`/approfondimenti/${a.slug}`}
                          data-testid={`ddi-related-${a.slug}`}
                          className="group flex items-center gap-4 border border-ink/12 bg-canvas p-3 hover:border-ink hover:bg-sand transition-all duration-300"
                        >
                          <div className="w-20 h-16 shrink-0 overflow-hidden">
                            <img src={a.cover} alt={a.coverAlt || a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-serif text-base leading-snug text-ink line-clamp-2">{a.title}</h3>
                            <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-ink/55 group-hover:text-ink group-hover:gap-2.5 transition-all duration-300">Leggi l'approfondimento <ArrowRight className="w-3.5 h-3.5" /></span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-10 flex flex-wrap items-center gap-5 text-sm">
                  <button onClick={back} data-testid="ddi-back" className="inline-flex items-center gap-2 text-ink/60 hover:text-ink"><ArrowLeft className="w-4 h-4" /> Indietro</button>
                  <button onClick={restart} data-testid="ddi-restart" className="inline-flex items-center gap-2 text-ink/45 hover:text-ink"><RotateCcw className="w-3.5 h-3.5" /> Ricomincia il percorso</button>
                </div>

                <div className="mt-10 border-t border-ink/10 pt-6 max-w-2xl">
                  <p className="text-[12px] text-ink/50 leading-relaxed">{DISCLAIMER}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
